/**
 * Transcript list and export controls (spec §18).
 *
 * Entries are virtualisation-free on purpose: a conversation is a few hundred
 * utterances at most, and the client already caps what it keeps. Virtualising
 * would add a dependency and a scroll-jump class of bug for no measurable win.
 * The list auto-scrolls only when the user is already at the bottom, so
 * scrolling up to re-read something is never yanked away.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import type { Transcript, TranscriptEntry, TranscriptExportFormat } from "@shared/types/transcript";
import { languageShortLabel } from "@shared/languages";
import { Badge, Button, IconButton, Segmented } from "./ui";
import { IconCopy, IconDownload, IconSpeaker, IconTrash } from "./icons";
import type { ExportOptions } from "../lib/export";
import { copyToClipboard, downloadTranscript } from "../lib/download";

interface Props {
  transcript: Transcript;
  onClear: () => void;
  onSpeak: (entry: TranscriptEntry) => void;
  /** Whether a voice exists for the current target language. */
  canSpeak: boolean;
  emptyHint: string;
}
const FORMATS: readonly { value: TranscriptExportFormat; label: string }[] = [
  { value: "txt", label: "TXT" },
  { value: "md", label: "MD" },
  { value: "json", label: "JSON" },
  { value: "srt", label: "SRT" },
  { value: "vtt", label: "VTT" },
];

export function TranscriptList({ transcript, onClear, onSpeak, canSpeak, emptyHint }: Props) {
  const [format, setFormat] = useState<TranscriptExportFormat>("srt");
  const [options, setOptions] = useState<ExportOptions>({
    format: "srt",
    bilingual: true,
    includeSpeakers: true,
    includeHeader: true,
    targetOnly: false,
  });
  const [copied, setCopied] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const pinnedRef = useRef(true);

  const entries = transcript.entries;
  const wordCount = useMemo(() => {
    let count = 0;
    for (const entry of entries) {
      count += entry.translatedText.trim() === "" ? 0 : entry.translatedText.trim().split(/\s+/).length;
    }
    return count;
  }, [entries]);

  // Follow the tail, unless the user has scrolled up to read something.
  useEffect(() => {
    if (!pinnedRef.current) return;
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [entries.length]);

  const onScroll = (): void => {
    const node = scrollRef.current;
    if (!node) return;
    const distance = node.scrollHeight - node.scrollTop - node.clientHeight;
    pinnedRef.current = distance < 80;
  };

  const doCopy = async (entry: TranscriptEntry): Promise<void> => {
    const text = entry.translatedText || entry.sourceText;
    const ok = await copyToClipboard(text);
    if (!ok) return;
    setCopied(entry.id);
    setTimeout(() => setCopied((current) => (current === entry.id ? null : current)), 1_600);
  };

  const doExport = (): void => {
    downloadTranscript(transcript, { ...options, format });
  };

  return (
    // Layout lives in `.transcript-panel` so the stacked mobile layout can
    // relax the height; an inline `height: 100%` here would outrank that rule.
    <section className="panel transcript-panel">
      <header className="panel__head">
        <div className="panel__title">
          Transcript
          {entries.length > 0 ? (
            <span className="dim">
              {entries.length} · {wordCount} words
            </span>
          ) : null}
        </div>
        <div className="row">
          <Segmented
            label="Export format"
            options={FORMATS}
            value={format}
            onChange={(value) => {
              setFormat(value);
              setOptions((current) => ({ ...current, format: value }));
            }}
          />
          <Button size="sm" iconOnly onClick={doExport} disabled={entries.length === 0} title={`Download as ${format.toUpperCase()}`} aria-label={`Download as ${format.toUpperCase()}`}>
            <IconDownload size={15} />
          </Button>
          <Button size="sm" variant="danger" iconOnly onClick={onClear} disabled={entries.length === 0} title="Clear the transcript" aria-label="Clear the transcript">
            <IconTrash size={15} />
          </Button>
        </div>
      </header>

      {entries.length === 0 ? (
        <div className="stream__empty" style={{ padding: "var(--space-l)" }}>
          <p className="dim" style={{ maxWidth: "32ch" }}>
            {emptyHint}
          </p>
        </div>
      ) : (
        <div className="scroller grow" ref={scrollRef} onScroll={onScroll} style={{ minHeight: 0 }}>
          <div className="entries">
            {entries.map((entry) => (
              <article key={entry.id} className={`entry entry--${entry.speaker.toLowerCase()}`}>
                <div className="stack" style={{ gap: 4, alignItems: "center" }}>
                  <span className="entry__time tabular">{entry.timestamp}</span>
                  <span className="entry__who" title={`Speaker ${entry.speaker}`}>
                    {entry.speaker}
                  </span>
                </div>

                <div style={{ minWidth: 0 }}>
                  <div className="entry__source" lang={entry.sourceLanguage}>
                    {entry.sourceText}
                  </div>
                  {entry.translatedText && entry.translatedText !== entry.sourceText ? (
                    <div className="entry__target" lang={entry.targetLanguage}>
                      {entry.translatedText}
                    </div>
                  ) : null}

                  <div className="entry__meta">
                    <Badge>{languageShortLabel(entry.sourceLanguage)}</Badge>
                    <span aria-hidden="true">→</span>
                    <Badge>{languageShortLabel(entry.targetLanguage)}</Badge>
                    {entry.translationMs > 0 ? <span className="tabular">{Math.round(entry.translationMs)} ms</span> : null}
                    <div className="grow" />
                    <IconButton label={copied === entry.id ? "Copied" : "Copy translation"} onClick={() => void doCopy(entry)}>
                      <IconCopy size={13} />
                    </IconButton>
                    {canSpeak ? (
                      <IconButton label="Speak this line" onClick={() => onSpeak(entry)}>
                        <IconSpeaker size={13} />
                      </IconButton>
                    ) : null}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      )}

      {entries.length > 0 ? (
        <footer className="panel__head" style={{ borderBottom: "none", borderTop: "1px solid var(--line)" }}>
          <div className="row" style={{ fontSize: "var(--step--2)" }}>
            <label className="row" style={{ gap: 6 }}>
              <input type="checkbox" checked={options.bilingual} onChange={(event) => setOptions((current) => ({ ...current, bilingual: event.target.checked }))} />
              <span className="dim">Include original</span>
            </label>
            <label className="row" style={{ gap: 6 }}>
              <input type="checkbox" checked={options.targetOnly} onChange={(event) => setOptions((current) => ({ ...current, targetOnly: event.target.checked }))} />
              <span className="dim">Target only (subtitles)</span>
            </label>
          </div>
        </footer>
      ) : null}
    </section>
  );
}
